import { AfterViewInit, ComponentRef, Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy, ViewContainerRef } from '@angular/core';
import { SpreadsheetExportService } from '../../services/spreadsheet-export.service';
import { TableExportButtonComponent } from './table-export-button.component';

@Directive({
  selector: '[ssTableExport]',
  standalone: false
})
export class TableExportDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input() ssTableExport: string | null | undefined = '';

  private static readonly hostClass = 'ss-table-export-host';
  private observer?: MutationObserver;
  private button?: ComponentRef<TableExportButtonComponent>;
  private headerCell?: HTMLElement;
  private refreshTimer?: ReturnType<typeof setTimeout>;
  private viewReady = false;

  constructor(
    private host: ElementRef<HTMLElement>,
    private viewContainerRef: ViewContainerRef,
    private ngZone: NgZone,
    private spreadsheetExport: SpreadsheetExportService) { }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.sync();
  }

  ngOnChanges(): void {
    if (this.viewReady) this.sync();
  }

  ngOnDestroy(): void {
    this.detach();
  }

  private sync(): void {
    if (this.ssTableExport) this.attach();
    else this.detach();
  }

  private attach(): void {
    if (!this.button) {
      this.button = this.viewContainerRef.createComponent(TableExportButtonComponent);
      this.button.instance.exportRequested.subscribe(() => this.exportTable());
    }
    if (!this.observer) {
      this.ngZone.runOutsideAngular(() => {
        this.observer = new MutationObserver(() => this.scheduleRefresh());
        this.observer.observe(this.host.nativeElement, { childList: true, subtree: true });
      });
    }
    this.refresh();
  }

  private detach(): void {
    this.observer?.disconnect();
    this.observer = undefined;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.headerCell?.classList.remove(TableExportDirective.hostClass);
    this.headerCell = undefined;
    this.button?.destroy();
    this.button = undefined;
  }

  private scheduleRefresh(): void {
    if (this.refreshTimer) return;
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = undefined;
      this.refresh();
    });
  }

  private refresh(): void {
    const table = this.table();
    const button = this.button;
    if (!table || !button) return;

    const headerRow = table.tHead?.rows[table.tHead.rows.length - 1];
    const lastCell = headerRow?.cells[headerRow.cells.length - 1];
    if (lastCell && !lastCell.contains(button.location.nativeElement)) {
      this.headerCell?.classList.remove(TableExportDirective.hostClass);
      lastCell.classList.add(TableExportDirective.hostClass);
      lastCell.appendChild(button.location.nativeElement);
      this.headerCell = lastCell;
    }

    const hasData = this.spreadsheetExport.readTable(table).rows.length > 0;
    if (button.instance.disabled === hasData) {
      button.instance.disabled = !hasData;
      button.changeDetectorRef.detectChanges();
    }
  }

  private exportTable(): void {
    const table = this.table();
    if (!table || !this.ssTableExport) return;
    this.spreadsheetExport.exportTable(this.ssTableExport, this.ssTableExport, table);
  }

  private table(): HTMLTableElement | null {
    return this.host.nativeElement.querySelector<HTMLTableElement>('table');
  }
}
